import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Search, User, Building2, Plus, Trash2, Eye, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  'Active': { bg: 'bg-[#F0FFF4]', text: 'text-[#15803D]', border: 'border-green-200' },
  'Inactive': { bg: 'bg-[#F7F7F7]', text: 'text-[#777]', border: 'border-[#E5E5E5]' },
  'Suspended': { bg: 'bg-[#FEF2F2]', text: 'text-[#D71920]', border: 'border-red-200' },
  'Deceased': { bg: 'bg-black/5', text: 'text-black/60', border: 'border-black/20' },
  'Transferred': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
};

const MEMBER_STATUSES = ['Active', 'Inactive', 'Suspended', 'Deceased', 'Transferred'];

const Members = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [schools, setSchools] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (showDeleted) params.append('show_deleted', 'true');
      if (statusFilter) params.append('status_filter', statusFilter);
      if (searchTerm.trim()) params.append('search', searchTerm.trim());

      const [membersRes, schoolsRes] = await Promise.all([
        axios.get(`${API_URL}/members?${params.toString()}`),
        axios.get(`${API_URL}/schools`)
      ]);
      setMembers(membersRes.data);
      const map = {};
      schoolsRes.data.forEach(s => { map[s.id] = s.name; });
      setSchools(map);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [showDeleted, statusFilter, searchTerm]);

  useEffect(() => {
    const t = setTimeout(() => { fetchData(); }, 300);
    return () => clearTimeout(t);
  }, [fetchData]);

  const handleDelete = async () => {
    if (deleteConfirmation !== 'PADAM' || !memberToDelete) {
      toast.error('Sila taip "PADAM" untuk sahkan');
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/members/${memberToDelete.id}`, { data: { confirmation: deleteConfirmation } });
      toast.success('Ahli telah dipadam');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memadam ahli');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
      setDeleteConfirmation('');
    }
  };

  const openDeleteDialog = (member, e) => {
    e.stopPropagation();
    setMemberToDelete(member);
    setDeleteDialogOpen(true);
  };

  const resetFilters = () => { setSearchTerm(''); setStatusFilter(''); setShowDeleted(false); };
  const hasActiveFilters = searchTerm || statusFilter || showDeleted;

  return (
    <AdminLayout>
      <div className="space-y-5 max-w-7xl">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="text-xs font-bold text-[#D71920] uppercase tracking-widest mb-1">Pentadbiran</div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B]" data-testid="page-title">Semua Ahli Silat</h1>
            <p className="text-sm text-[#777] mt-0.5">Carian global semua ahli di Sarawak</p>
          </div>
          <button
            onClick={() => navigate('/admin/members/new')}
            className="flex items-center gap-2 bg-[#D71920] hover:bg-[#b8161c] text-white text-sm font-semibold px-4 py-2 rounded transition-colors self-start sm:self-auto"
            data-testid="add-member-button"
          >
            <Plus size={14} /> Daftar Ahli
          </button>
        </div>

        <div className="h-px bg-[#E5E5E5]" />

        {/* Filters */}
        <div className="bg-white rounded border border-[#E5E5E5] p-4 space-y-3">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
            <Input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Cari nama, no ahli atau IC..."
              className="pl-9 border-[#E5E5E5] text-sm"
              data-testid="search-input"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {['', ...MEMBER_STATUSES].map(s => (
              <button
                key={s || 'all'}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 text-xs font-semibold rounded border transition-colors ${
                  statusFilter === s
                    ? 'bg-[#0B0B0B] text-white border-[#0B0B0B]'
                    : 'bg-white text-[#555] border-[#E5E5E5] hover:border-[#0B0B0B]'
                }`}
                data-testid={`filter-status-${s || 'all'}`}
              >
                {s || 'Semua Status'}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-[#F7F7F7]">
            <div className="flex items-center gap-2">
              <Switch id="show-deleted" checked={showDeleted} onCheckedChange={setShowDeleted} data-testid="show-deleted-toggle" />
              <Label htmlFor="show-deleted" className="text-xs text-[#555] cursor-pointer">Tunjuk yang dipadam</Label>
            </div>
            {hasActiveFilters && (
              <button onClick={resetFilters} className="flex items-center gap-1 text-xs text-[#D71920] font-semibold hover:underline" data-testid="reset-filters-button">
                <X size={12} /> Reset
              </button>
            )}
          </div>
        </div>

        {/* Members — Table on md+, Cards on mobile */}
        {loading ? (
          <div className="text-center py-16 text-sm text-[#999] animate-pulse">Memuatkan ahli...</div>
        ) : members.length === 0 ? (
          <div className="text-center py-16 bg-white rounded border border-[#E5E5E5]">
            <User size={36} className="text-[#E5E5E5] mx-auto mb-3" />
            <p className="text-sm text-[#777]">Tiada ahli dijumpai</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block bg-white rounded border border-[#E5E5E5] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F7F7F7] border-b border-[#E5E5E5]">
                    <tr>
                      {['No. Ahli', 'Nama', 'IC (Tertutup)', 'Perguruan', 'Status', ''].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-bold text-[#555] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F7F7F7]">
                    {members.map((member) => {
                      const sc = statusConfig[member.status] || statusConfig['Inactive'];
                      return (
                        <tr
                          key={member.id}
                          className={`transition-colors ${member.is_deleted ? 'opacity-50 bg-black/3' : 'hover:bg-[#FEF2F2]/30'}`}
                          data-testid={`member-row-${member.id}`}
                        >
                          <td className="px-4 py-3">
                            <span className="font-mono text-xs font-bold text-[#D71920]">{member.member_no}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-full bg-[#D71920]/8 flex items-center justify-center flex-shrink-0">
                                <User size={14} className="text-[#D71920]" />
                              </div>
                              <span className="text-sm font-medium text-[#0B0B0B]">{member.full_name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-[#777] font-mono">{member.ic_masked}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 text-xs text-[#555]">
                              <Building2 size={12} className="text-[#999]" />
                              <span className="truncate max-w-[140px]">{schools[member.school_id] || '—'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1">
                              <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold border ${sc.bg} ${sc.text} ${sc.border}`}>
                                {member.status}
                              </span>
                              {member.is_deleted && (
                                <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-[10px] font-bold bg-black/10 text-black/60 border border-black/20">
                                  <Trash2 size={8} />Dipadam
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => navigate(`/admin/members/${member.id}`)}
                                className="p-1.5 rounded text-[#D71920] hover:bg-[#FEF2F2] transition-colors"
                                title="Lihat profil"
                                data-testid={`view-member-${member.id}`}
                              >
                                <Eye size={15} />
                              </button>
                              {!member.is_deleted && (
                                <button
                                  onClick={(e) => openDeleteDialog(member, e)}
                                  className="p-1.5 rounded text-red-500 hover:bg-red-50 transition-colors"
                                  title="Padam"
                                  data-testid={`delete-member-${member.id}`}
                                >
                                  <Trash2 size={15} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {members.map((member) => {
                const sc = statusConfig[member.status] || statusConfig['Inactive'];
                return (
                  <div
                    key={member.id}
                    className={`bg-white rounded border p-4 ${member.is_deleted ? 'opacity-50 border-[#E5E5E5]' : 'border-[#E5E5E5]'}`}
                    data-testid={`member-card-mobile-${member.id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <div className="w-9 h-9 rounded-full bg-[#D71920]/8 flex items-center justify-center flex-shrink-0">
                          <User size={15} className="text-[#D71920]" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-sm text-[#0B0B0B] leading-tight truncate">{member.full_name}</p>
                          <p className="font-mono text-[10px] text-[#D71920] font-bold mt-0.5">{member.member_no}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold border flex-shrink-0 whitespace-nowrap ${sc.bg} ${sc.text} ${sc.border}`}>
                        {member.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-[#777] mb-3">
                      <div className="flex items-center gap-1">
                        <Building2 size={11} />
                        <span className="truncate max-w-[140px]">{schools[member.school_id] || '—'}</span>
                      </div>
                      <span className="font-mono">{member.ic_masked}</span>
                    </div>
                    <div className="flex gap-2 pt-2 border-t border-[#F7F7F7]">
                      <button
                        onClick={() => navigate(`/admin/members/${member.id}`)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded border border-[#E5E5E5] text-xs font-semibold text-[#D71920] hover:border-[#D71920]/30 hover:bg-[#FEF2F2]/30 transition-colors"
                        data-testid={`view-member-${member.id}`}
                      >
                        <Eye size={13} /> Lihat Profil
                      </button>
                      {!member.is_deleted && (
                        <button
                          onClick={(e) => openDeleteDialog(member, e)}
                          className="px-3 py-2 rounded border border-red-200 text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors"
                          data-testid={`delete-member-${member.id}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-700">Padam Ahli</DialogTitle>
              <DialogDescription>
                Anda akan memadam ahli <strong>{memberToDelete?.full_name}</strong>.
                Taip <strong className="text-red-700">PADAM</strong> untuk sahkan.
              </DialogDescription>
            </DialogHeader>
            <Input
              value={deleteConfirmation}
              onChange={e => setDeleteConfirmation(e.target.value.toUpperCase())}
              className="border-red-200 focus:border-red-400"
              placeholder="Taip PADAM"
              data-testid="delete-confirmation-input"
            />
            <DialogFooter>
              <button
                onClick={() => { setDeleteDialogOpen(false); setMemberToDelete(null); setDeleteConfirmation(''); }}
                className="px-4 py-2 text-sm border border-[#E5E5E5] rounded hover:bg-[#F7F7F7] transition-colors"
              >
                Batal
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteConfirmation !== 'PADAM' || deleting}
                className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded transition-colors disabled:opacity-50"
                data-testid="confirm-delete-button"
              >
                {deleting ? 'Memadam...' : 'Padam Ahli'}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="text-center text-xs text-[#999]" data-testid="members-count">
          {members.length} ahli dijumpai
        </div>
      </div>
    </AdminLayout>
  );
};

export default Members;
